import React, { useState, useEffect, useRef, useCallback } from "react";
import FloatingCard from "../../../ui/FloatingCard";
import ScoringModeToggle from "../../live/components/ScoringModeToggle";
import InlinePlayerGrid from "../common/InlinePlayerGrid";
import PermissionBadge from "../common/PermissionBadge";
import BasicScoreBox from "../common/BasicScoreBox";
import usePeriodClockSync from "../common/usePeriodClockSync";
import {
  getFootballFormat,
  getFootballPhaseLabel,
  getFootballPhaseEndAction,
  getPenaltyShootoutState,
  FOOTBALL_PHASE,
} from "../../../../utils/sportRules";

const fmtTime = (s) => {
  const total = Math.max(0, Math.ceil(s));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const HALF_KEY   = (id) => `football_half_${id}`;
const DEPART_KEY = (id) => `football_depart_${id}`;
const saveHalf   = (id, data) => { try { localStorage.setItem(HALF_KEY(id), JSON.stringify(data)); } catch {} };
const loadHalf   = (id) => { try { const r = localStorage.getItem(HALF_KEY(id)); return r ? JSON.parse(r) : null; } catch { return null; } };
const saveDepart  = (id, ts) => { try { localStorage.setItem(DEPART_KEY(id), String(ts)); } catch {} };
const loadDepart  = (id) => { try { const r = localStorage.getItem(DEPART_KEY(id)); return r ? parseInt(r, 10) : null; } catch { return null; } };
const clearDepart = (id) => { try { localStorage.removeItem(DEPART_KEY(id)); } catch {} };

const PENS_KEY   = (id) => `football_penalties_${id}`;
const savePens   = (id, data) => { try { localStorage.setItem(PENS_KEY(id), JSON.stringify(data)); } catch {} };
const loadPens   = (id) => { try { const r = localStorage.getItem(PENS_KEY(id)); return r ? JSON.parse(r) : null; } catch { return null; } };

// IFAB: 2 x 45 min halves; a level knockout game goes to extra time
// (2 x 15 min) and then a penalty shootout.
const getFootballConfig = (match) => getFootballFormat(match);

export { getFootballConfig };

const FootballScoreboard = ({
  match,
  currentScore,
  isReadOnlyMode,
  connected,
  isMatchActive,
  onScoreIncrease,
  onScoreDecrease,
  onResetScore,
  onToggleTimeout,
  onCompleteMatch,
  onStartMatch,
  isTimeoutActive,
  scoringMode,
  onScoringModeChange,
  advancedSupported = false,
  announcerMuted,
  onAnnouncerToggleMute,
  onAnnouncerRepeat,
  p1Players = [],
  p2Players = [],
  onPlayerSelect,
  // Server clock (LS-B09): followed by viewers, stored by the scorer
  serverClock = null,
  onClockChange,
  onPeriodChange,
}) => {
  const matchId = match?.id;
  const { halfSeconds, extraTimeSeconds } = getFootballConfig(match);
  // Whether this device had its own saved clock when the board opened
  const [hasLocalClock] = useState(() => loadHalf(matchId) != null);

  const [currentHalf, setCurrentHalfRaw] = useState(() => loadHalf(matchId)?.currentHalf ?? 1);
  const [timeLeft, setTimeLeft] = useState(() => loadHalf(matchId)?.timeLeft ?? halfSeconds);
  const [timerRunning, setTimerRunning] = useState(false);
  const [halfEnded, setHalfEnded] = useState(false);
  const [halfHasStarted, setHalfHasStarted] = useState(false);
  // Penalty shootout: kicks in order taken, and which team kicks first
  const [penaltyKicks, setPenaltyKicks] = useState(() => loadPens(matchId)?.kicks ?? []);
  const [penaltyFirstKicker, setPenaltyFirstKicker] = useState(() => loadPens(matchId)?.firstKicker ?? "p1");

  const timerRef        = useRef(null);
  const timeLeftRef     = useRef(timeLeft);
  const matchIdRef      = useRef(matchId);
  const currentHalfRef  = useRef(currentHalf);
  const isRunningRef          = useRef(false);
  const resumeAfterTimeoutRef  = useRef(false);
  timeLeftRef.current     = timeLeft;
  matchIdRef.current      = matchId;
  currentHalfRef.current  = currentHalf;

  // Re-sync half/timeLeft once matchId resolves (async match load).
  // Also calculates elapsed time for "Keep Running & Leave" returns.
  useEffect(() => {
    if (!matchId) return;
    const saved = loadHalf(matchId);
    if (!saved) return;

    let resolvedTimeLeft = saved.timeLeft ?? halfSeconds;

    const depart = loadDepart(matchId);
    if (depart && !saved.timerPaused) {
      const elapsed = Math.floor((Date.now() - depart) / 1000);
      resolvedTimeLeft = Math.max(0, resolvedTimeLeft - elapsed);
      clearDepart(matchId);
      if (resolvedTimeLeft <= 0) {
        setCurrentHalfRaw(saved.currentHalf);
        currentHalfRef.current = saved.currentHalf;
        setTimeLeft(0); timeLeftRef.current = 0;
        setHalfEnded(true);
        saveHalf(matchId, { ...saved, timeLeft: 0, timerPaused: true, halfEnded: true });
        return;
      }
    }

    setCurrentHalfRaw(saved.currentHalf);
    currentHalfRef.current = saved.currentHalf;
    setTimeLeft(resolvedTimeLeft);
    timeLeftRef.current = resolvedTimeLeft;
    const pens = loadPens(matchId);
    if (pens) { setPenaltyKicks(pens.kicks || []); setPenaltyFirstKicker(pens.firstKicker || "p1"); }
    if (saved.halfEnded) {
      setHalfEnded(true);
    } else if (resolvedTimeLeft < (saved.currentHalf >= FOOTBALL_PHASE.ET_FIRST ? extraTimeSeconds : halfSeconds)) {
      setHalfHasStarted(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  useEffect(() => {
    if (!isMatchActive) {
      clearInterval(timerRef.current); timerRef.current = null;
      isRunningRef.current = false;
      resumeAfterTimeoutRef.current = false;
      setTimerRunning(false);
    }
  }, [isMatchActive]);

  // Timeout: pause clock without losing "wants to run" state; auto-resume when timeout ends.
  useEffect(() => {
    if (isTimeoutActive) {
      resumeAfterTimeoutRef.current = isRunningRef.current;
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; setTimerRunning(false); }
    } else if (resumeAfterTimeoutRef.current) {
      resumeAfterTimeoutRef.current = false;
      if (!halfEnded) startTimer();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimeoutActive]);

  // On unmount: save state + departure timestamp if timer was running
  useEffect(() => () => {
    const wasRunning = isRunningRef.current;
    clearInterval(timerRef.current);
    const id = matchIdRef.current;
    if (!id) return;
    saveHalf(id, { currentHalf: currentHalfRef.current, timeLeft: timeLeftRef.current, timerPaused: !wasRunning });
    if (wasRunning) saveDepart(id, Date.now());
  }, []);

  const startTimer = useCallback(() => {
    if (timerRef.current || halfEnded) return;
    isRunningRef.current = true;
    setHalfHasStarted(true);
    setTimerRunning(true);
    timerRef.current = setInterval(() => {
      const next = timeLeftRef.current - 1;
      if (next <= 0) {
        clearInterval(timerRef.current); timerRef.current = null;
        isRunningRef.current = false;
        setTimeLeft(0); timeLeftRef.current = 0;
        setTimerRunning(false); setHalfEnded(true);
        saveHalf(matchIdRef.current, { currentHalf: currentHalfRef.current, timeLeft: 0, halfEnded: true, timerPaused: true });
      } else {
        setTimeLeft(next); timeLeftRef.current = next;
        if (next % 15 === 0) saveHalf(matchIdRef.current, { currentHalf: currentHalfRef.current, timeLeft: next, timerPaused: false });
      }
    }, 1000);
  }, [halfEnded]);

  const pauseTimer = useCallback(() => {
    clearInterval(timerRef.current); timerRef.current = null;
    isRunningRef.current = false;
    setTimerRunning(false);
    saveHalf(matchIdRef.current, { currentHalf: currentHalfRef.current, timeLeft: timeLeftRef.current, timerPaused: true });
  }, []);

  const startTimerRef = useRef(startTimer);
  startTimerRef.current = startTimer;
  // Penalties have no clock
  const periodLengthSec = useCallback((p) => (
    p === FOOTBALL_PHASE.PENALTIES ? 0 : p >= FOOTBALL_PHASE.ET_FIRST ? extraTimeSeconds : halfSeconds
  ), [extraTimeSeconds, halfSeconds]);

  // Take the half and time left from the server clock (viewers; a scorer's fresh device)
  const applyServerClock = useCallback(({ period: p, timeLeft: t, running, viewer }) => {
    setCurrentHalfRaw(p);
    currentHalfRef.current = p;
    setTimeLeft(t);
    timeLeftRef.current = t;
    const length = periodLengthSec(p);
    setHalfEnded(length > 0 && t <= 0);
    setHalfHasStarted(length === 0 || t < length);
    if (viewer) {
      setTimerRunning(!!running);
      return;
    }
    saveHalf(matchIdRef.current, { currentHalf: p, timeLeft: t, halfEnded: length > 0 && t <= 0, timerPaused: !running });
    if (running && t > 0) setTimeout(() => startTimerRef.current(), 0);
  }, [periodLengthSec]);

  const startSecondHalf = () => {
    setCurrentHalfRaw(2); currentHalfRef.current = 2;
    setTimeLeft(halfSeconds); timeLeftRef.current = halfSeconds;
    setHalfEnded(false); setHalfHasStarted(false);
    resumeAfterTimeoutRef.current = false;
    saveHalf(matchId, { currentHalf: 2, timeLeft: halfSeconds, halfEnded: false, timerPaused: true });
  };

  // Extra time: two halves of 15 minutes (phase 3 and 4)
  const startExtraTimeHalf = (phase) => {
    setCurrentHalfRaw(phase); currentHalfRef.current = phase;
    setTimeLeft(extraTimeSeconds); timeLeftRef.current = extraTimeSeconds;
    setHalfEnded(false); setHalfHasStarted(false);
    resumeAfterTimeoutRef.current = false;
    saveHalf(matchId, { currentHalf: phase, timeLeft: extraTimeSeconds, halfEnded: false, timerPaused: true });
  };
  const startExtraTime = () => startExtraTimeHalf(FOOTBALL_PHASE.ET_FIRST);
  const startExtraTimeSecondHalf = () => startExtraTimeHalf(FOOTBALL_PHASE.ET_SECOND);

  // Penalty shootout (phase 5) — no clock, kicks recorded one by one
  const startPenalties = () => {
    clearInterval(timerRef.current); timerRef.current = null;
    isRunningRef.current = false;
    setTimerRunning(false);
    setCurrentHalfRaw(FOOTBALL_PHASE.PENALTIES); currentHalfRef.current = FOOTBALL_PHASE.PENALTIES;
    setTimeLeft(0); timeLeftRef.current = 0;
    setHalfEnded(false); setHalfHasStarted(true);
    setPenaltyKicks([]);
    savePens(matchId, { kicks: [], firstKicker: penaltyFirstKicker });
    saveHalf(matchId, { currentHalf: FOOTBALL_PHASE.PENALTIES, timeLeft: 0, halfEnded: false, timerPaused: true });
  };

  const recordPenalty = (scored) => {
    const state = getPenaltyShootoutState(penaltyKicks, penaltyFirstKicker);
    if (state.winner || !state.nextKicker) return;
    const kicks = [...penaltyKicks, { team: state.nextKicker, scored }];
    setPenaltyKicks(kicks);
    savePens(matchId, { kicks, firstKicker: penaltyFirstKicker });
  };

  const undoPenalty = () => {
    const kicks = penaltyKicks.slice(0, -1);
    setPenaltyKicks(kicks);
    savePens(matchId, { kicks, firstKicker: penaltyFirstKicker });
  };

  const togglePenaltyFirstKicker = () => {
    if (penaltyKicks.length > 0) return;
    const next = penaltyFirstKicker === "p1" ? "p2" : "p1";
    setPenaltyFirstKicker(next);
    savePens(matchId, { kicks: [], firstKicker: next });
  };

  // End the current half/period early without waiting for clock
  const handleCompleteCurrentHalf = () => {
    if (!isMatchActive) return;
    clearInterval(timerRef.current); timerRef.current = null;
    isRunningRef.current = false;
    setTimerRunning(false);
    setTimeLeft(0); timeLeftRef.current = 0;
    setHalfEnded(true);
    saveHalf(matchId, { currentHalf: currentHalfRef.current, timeLeft: 0, halfEnded: true, timerPaused: true });
  };

  const halfLabel = getFootballPhaseLabel(currentHalf);

  usePeriodClockSync({
    isReadOnlyMode,
    isMatchActive,
    serverClock,
    onClockChange,
    onPeriodChange,
    period: currentHalf,
    periodLabel: halfLabel,
    periodLengthSec,
    timeLeft,
    timerRunning,
    hasLocalState: hasLocalClock,
    apply: applyServerClock,
  });
  const p1Score = currentScore?.participant1 || 0;
  const p2Score = currentScore?.participant2 || 0;
  const dit = (match?.matchConfig || match?.match_config)?.drop_in_teams;
  const p1Name = dit ? (dit.team_a?.name || "Team A") : (match?.participant_1_name || match?.participant1Name || "Team 1");
  const p2Name = dit ? (dit.team_b?.name || "Team B") : (match?.participant_2_name || match?.participant2Name || "Team 2");

  const inPenalties = currentHalf === FOOTBALL_PHASE.PENALTIES;
  const phaseEndAction = getFootballPhaseEndAction(currentHalf, p1Score, p2Score);
  const shootout = getPenaltyShootoutState(penaltyKicks, penaltyFirstKicker);

  // Complete a game decided on penalties: the goal score stays level, the
  // shootout decides the winner and is saved alongside the score.
  const completeAfterPenalties = () => {
    if (!shootout.winner || !onCompleteMatch) return;
    const w = shootout.winner;
    const p1Id   = match?.participant1Id   || match?.participant_1_id;
    const p2Id   = match?.participant2Id   || match?.participant_2_id;
    const p1Type = match?.participant1Type || match?.participant_1_type || "team";
    const p2Type = match?.participant2Type || match?.participant_2_type || "team";
    onCompleteMatch({
      cancelled: false,
      winner: { id: w === "p1" ? p1Id : p2Id, type: w === "p1" ? p1Type : p2Type },
      winnerName: `${w === "p1" ? p1Name : p2Name} (${shootout.scored.p1}–${shootout.scored.p2} on penalties)`,
      participant1Score: p1Score,
      participant2Score: p2Score,
      penalties: { p1: shootout.scored.p1, p2: shootout.scored.p2, kicks: penaltyKicks, first_kicker: penaltyFirstKicker },
    });
  };

  const isAdvancedInline = scoringMode === 'advanced' && advancedSupported && !!onPlayerSelect;
  const addBlocked = !isMatchActive || currentHalf === FOOTBALL_PHASE.PENALTIES;
  const p1SubBlocked = !isMatchActive || p1Score <= 0;
  const p2SubBlocked = !isMatchActive || p2Score <= 0;

  // Called as a function so the player icons are not remounted on every render
  const renderGoalControls = (participant, score, players) => {
    if (isReadOnlyMode) return null;
    if (isAdvancedInline) {
      const subBlocked = !isMatchActive || score <= 0;
      return (
        <div className="vb-adv-inline">
          <InlinePlayerGrid participant={participant} players={players} isDisabled={addBlocked} onPlayerSelect={onPlayerSelect} />
          <button className="vb-deduct-btn" onClick={() => onScoreDecrease(participant, 1)} disabled={subBlocked}>−1</button>
        </div>
      );
    }
    // Basic mode is rendered via the clickable BasicScoreBox in the main JSX below
    return null;
  };

  return (
    <FloatingCard variant="gradient" size="large" className="score-display-websocket delay-3" glow={isMatchActive}>
      <div className="score-header-websocket">
        <div className="score-header-top">
          <h3>⚽ Football — {halfLabel}</h3>
          <div className="score-header-top-right">
            {!isReadOnlyMode && onScoringModeChange && (
              <ScoringModeToggle mode={scoringMode || "basic"} onChange={onScoringModeChange} sportSupported={advancedSupported} disabled={!isMatchActive} />
            )}
            <PermissionBadge isReadOnlyMode={isReadOnlyMode} />
          </div>
        </div>

        {/* Score Announcer pill — shown to editors and viewers alike */}
        {isMatchActive && onAnnouncerToggleMute && (
          <div className="score-announcer-bar">
            <button
              className={`announcer-btn${announcerMuted ? ' muted' : ''}`}
              onClick={onAnnouncerToggleMute}
              title={announcerMuted ? 'Unmute score announcer' : 'Mute score announcer'}
            >
              {announcerMuted ? '🔇' : '🔊'}
            </button>
            <span className="announcer-label">Score Announcer</span>
            <button className="announcer-btn" onClick={onAnnouncerRepeat} title="Repeat last announcement">🔁</button>
          </div>
        )}

        {!isReadOnlyMode && (
          <div className="live-match-quick-controls">
            {match?.status === "scheduled" && onStartMatch && <button className="live-ctrl-btn live-ctrl-start" onClick={onStartMatch}>🎬 Start Match</button>}
            {isMatchActive && (
              <>
                {/* Clock controls — hidden while timeout active */}
                {!isTimeoutActive && !halfEnded && !inPenalties && (
                  <>
                    {!halfHasStarted && (
                      <button className="live-ctrl-btn live-ctrl-start"
                        onClick={() => { if (match?.status === 'scheduled' && onStartMatch) onStartMatch(); startTimer(); }}>
                        ▶️ Start {halfLabel}
                      </button>
                    )}
                    {halfHasStarted && !timerRunning && (
                      <button className="live-ctrl-btn live-ctrl-start" onClick={startTimer}>▶️ Resume {halfLabel}</button>
                    )}
                    {timerRunning && (
                      <button className="live-ctrl-btn live-ctrl-timeout" onClick={pauseTimer}>⏸️ Pause</button>
                    )}
                    {halfHasStarted && (
                      <button className="live-ctrl-btn live-ctrl-end-period" onClick={handleCompleteCurrentHalf}
                        title={`End ${halfLabel} now`}>🔚 End {halfLabel}</button>
                    )}
                  </>
                )}
                {/* Timeout toggle — always visible */}
                {!halfEnded && (
                  <button className={`live-ctrl-btn live-ctrl-timeout${isTimeoutActive ? ' active' : ''}`} onClick={onToggleTimeout}>
                    {isTimeoutActive ? '▶️ End Timeout' : '⏸️ Timeout'}
                  </button>
                )}
                <button className="live-ctrl-btn live-ctrl-reset" onClick={onResetScore}>🔄 Reset</button>
                <button className="live-ctrl-btn live-ctrl-complete" onClick={() => onCompleteMatch && onCompleteMatch()}>🏁 Complete</button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Half ended prompt */}
      {halfEnded && !inPenalties && isMatchActive && !isReadOnlyMode && (
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap", padding: "0.75rem 1rem", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: "0.5rem", marginBottom: "0.75rem" }}>
          <span style={{ color: "#f59e0b", fontWeight: 600 }}>⏱️ {halfLabel} ended</span>
          {phaseEndAction === "second_half" && (
            <button onClick={startSecondHalf} style={{ padding: "0.4rem 1rem", background: "rgba(99,102,241,0.3)", border: "1px solid #6366f1", color: "#a78bfa", borderRadius: "0.4rem", cursor: "pointer", fontWeight: 600 }}>
              Start 2nd Half →
            </button>
          )}
          {phaseEndAction === "et_second_half" && (
            <button onClick={startExtraTimeSecondHalf} style={{ padding: "0.4rem 1rem", background: "rgba(99,102,241,0.3)", border: "1px solid #6366f1", color: "#a78bfa", borderRadius: "0.4rem", cursor: "pointer", fontWeight: 600 }}>
              Start Extra Time 2nd Half →
            </button>
          )}
          {phaseEndAction === "extra_time" && (
            <button onClick={startExtraTime} style={{ padding: "0.4rem 1rem", background: "rgba(245,158,11,0.25)", border: "1px solid #f59e0b", color: "#f59e0b", borderRadius: "0.4rem", cursor: "pointer", fontWeight: 600 }}>
              🔄 Extra Time (2 × {Math.round(extraTimeSeconds / 60)} min)
            </button>
          )}
          {phaseEndAction === "penalties" && (
            <button onClick={startPenalties} style={{ padding: "0.4rem 1rem", background: "rgba(245,158,11,0.25)", border: "1px solid #f59e0b", color: "#f59e0b", borderRadius: "0.4rem", cursor: "pointer", fontWeight: 600 }}>
              🥅 Penalty Shootout
            </button>
          )}
          {/* A level game after 90 minutes may end as a draw (league play) */}
          {(phaseEndAction === "end_game" || phaseEndAction === "extra_time") && (
            <button onClick={() => onCompleteMatch && onCompleteMatch()} style={{ padding: "0.4rem 1rem", background: "rgba(239,68,68,0.25)", border: "1px solid #ef4444", color: "#ef4444", borderRadius: "0.4rem", cursor: "pointer", fontWeight: 600 }}>
              🏁 End Game
            </button>
          )}
        </div>
      )}

      {/* Penalty shootout */}
      {inPenalties && (
        <div data-testid="penalty-shootout" style={{ padding: "0.75rem 1rem", background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.35)", borderRadius: "0.5rem", marginBottom: "0.75rem" }}>
          <div style={{ textAlign: "center", color: "#a78bfa", fontWeight: 700, marginBottom: "0.5rem" }}>
            🥅 Penalty Shootout — {p1Name} {shootout.scored.p1}–{shootout.scored.p2} {p2Name}
            {shootout.suddenDeath && !shootout.winner ? " · Sudden death" : ""}
          </div>
          {["p1", "p2"].map((side) => (
            <div key={side} style={{ display: "flex", gap: "0.35rem", alignItems: "center", justifyContent: "center", marginBottom: "0.3rem" }}>
              <span style={{ minWidth: "6rem", textAlign: "right", color: "#e2e8f0", fontSize: "0.8rem" }}>{side === "p1" ? p1Name : p2Name}</span>
              {penaltyKicks.filter((k) => k.team === side).map((k, i) => (
                <span key={i} aria-label={k.scored ? "scored" : "missed"} style={{ fontSize: "0.85rem" }}>{k.scored ? "✅" : "❌"}</span>
              ))}
            </div>
          ))}
          {!isReadOnlyMode && isMatchActive && (
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", flexWrap: "wrap", marginTop: "0.5rem" }}>
              {shootout.winner ? (
                <>
                  <span style={{ color: "#22c55e", fontWeight: 700 }}>
                    🏆 {shootout.winner === "p1" ? p1Name : p2Name} win {Math.max(shootout.scored.p1, shootout.scored.p2)}–{Math.min(shootout.scored.p1, shootout.scored.p2)} on penalties
                  </span>
                  <button className="live-ctrl-btn live-ctrl-complete" onClick={completeAfterPenalties}>🏆 Complete Match</button>
                </>
              ) : (
                <>
                  <span style={{ color: "#94a3b8", fontSize: "0.8rem", alignSelf: "center" }}>
                    Kick: {shootout.nextKicker === "p1" ? p1Name : p2Name}
                  </span>
                  <button className="live-ctrl-btn live-ctrl-start" onClick={() => recordPenalty(true)}>⚽ Scored</button>
                  <button className="live-ctrl-btn live-ctrl-reset" onClick={() => recordPenalty(false)}>❌ Missed</button>
                  {penaltyKicks.length === 0 && (
                    <button className="live-ctrl-btn" onClick={togglePenaltyFirstKicker}>↔ {penaltyFirstKicker === "p1" ? p1Name : p2Name} kicks first (switch)</button>
                  )}
                </>
              )}
              {penaltyKicks.length > 0 && (
                <button className="live-ctrl-btn" onClick={undoPenalty}>↩ Undo kick</button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Timer */}
      {!inPenalties && (
      <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
        <span style={{ fontSize: "2rem", fontWeight: 800, color: timeLeft <= 60 && timerRunning ? "#ef4444" : "#e2e8f0", fontVariantNumeric: "tabular-nums" }}>
          {fmtTime(timeLeft)}
        </span>
        <span style={{ marginLeft: "0.75rem", fontSize: "0.8rem", color: "#64748b" }}>
          {halfLabel}
        </span>
      </div>
      )}

      {/* Scoreboard */}
      <div className="participants-websocket">
        <div className="vb-participant-col">
          <div className="vb-team-name">{p1Name}</div>
          {!isReadOnlyMode && !isAdvancedInline ? (
            <BasicScoreBox
              participantClassName="participant-1"
              score={p1Score}
              disabled={addBlocked}
              subDisabled={p1SubBlocked}
              onIncrease={() => onScoreIncrease("participant1", 1)}
              onDecrease={() => onScoreDecrease("participant1", 1)}
              addLabel={`Add 1 point for ${p1Name}`}
              subLabel={`Subtract 1 point for ${p1Name}`}
            />
          ) : (
            <div className="participant-websocket participant-1">
              <div className="participant-score-large">{p1Score}</div>
              {renderGoalControls("participant1", p1Score, p1Players)}
            </div>
          )}
        </div>
        <div className="vs-divider-websocket">
          <div className="vs-circle-websocket">VS</div>
          <div className="vs-duration" style={{ fontSize: "0.9rem", color: "#64748b" }}>{halfLabel}</div>
        </div>
        <div className="vb-participant-col">
          <div className="vb-team-name">{p2Name}</div>
          {!isReadOnlyMode && !isAdvancedInline ? (
            <BasicScoreBox
              participantClassName="participant-2"
              score={p2Score}
              disabled={addBlocked}
              subDisabled={p2SubBlocked}
              onIncrease={() => onScoreIncrease("participant2", 1)}
              onDecrease={() => onScoreDecrease("participant2", 1)}
              addLabel={`Add 1 point for ${p2Name}`}
              subLabel={`Subtract 1 point for ${p2Name}`}
            />
          ) : (
            <div className="participant-websocket participant-2">
              <div className="participant-score-large">{p2Score}</div>
              {renderGoalControls("participant2", p2Score, p2Players)}
            </div>
          )}
        </div>
      </div>
    </FloatingCard>
  );
};

export default FootballScoreboard;

